import { RenderSettings } from "./useTokenRenderer";
import { Canvg } from "canvg";
import { Dispatch, SetStateAction } from "react";
import { getGraphQlClient } from "@/shared/artblocks";
import { graphql } from "@/gql/index";

export const unsupportedLibraries = ["twemoji", "zdog", "processing"];
export const generatorIframeID = "art-blocks-rendering-tool";

export const unsupportedCustomRendererProjects = [
  "0x059edd72cd353df5106d2b9cc5ab83a52287ac3a-1", //Genesis
  "0x059edd72cd353df5106d2b9cc5ab83a52287ac3a-2", // Construction Token
  "0xa7d8d9ef8d8ce8992df33d8b8cf4aebabd5bd270-76", // Dino Pals
  "0xa7d8d9ef8d8ce8992df33d8b8cf4aebabd5bd270-130", // Obicera
  "0xa7d8d9ef8d8ce8992df33d8b8cf4aebabd5bd270-172", // Rituals
  "0xa7d8d9ef8d8ce8992df33d8b8cf4aebabd5bd270-233", // Chimera
  "0xa7d8d9ef8d8ce8992df33d8b8cf4aebabd5bd270-286", // Drifting
  "0xa7d8d9ef8d8ce8992df33d8b8cf4aebabd5bd270-304", // Antifragile
  "0xa7d8d9ef8d8ce8992df33d8b8cf4aebabd5bd270-324", // Woah la Coaster
  "0x99a9b7c1116f9ceeb1652de04d5969cce509b069-385", // Dipolar
  "0x99a9b7c1116f9ceeb1652de04d5969cce509b069-426", // Cargo
  "0x99a9b7c1116f9ceeb1652de04d5969cce509b069-457", // Dopamine Machines
  "0x99a9b7c1116f9ceeb1652de04d5969cce509b069-486", // Proscenium
  "0x99a9b7c1116f9ceeb1652de04d5969cce509b069-489", // Balance
  "0x0a1bbd57033f57e7b6743621b79fcb9eb2ce3676-25", // 1935
  "0x28f2d3805652fb5d359486dffb7d08320d403240-1", // Sliderverse
  "0xbdde08bd57e5c9fd563ee7ac61618cb2ecdc0ce0-5", // CryptoMexa
  "0xbdde08bd57e5c9fd563ee7ac61618cb2ecdc0ce0-6", // CryptoTokyoite
  "0xbdde08bd57e5c9fd563ee7ac61618cb2ecdc0ce0-8", // CryptoParisian
  "0x0a1bbd57033f57e7b6743621b79fcb9eb2ce3676-74", // Descent
  "0x64780ce53f6e966e18a22af13a2f97369580ec11-0", // Petro National
  "0x1353fd9d3dc70d1a18149c8fb2adb4fb906de4e8-4", // Lycorises
];

const GQL_PROJECT_QUERY = graphql(`
  query GetProjectQuery($projectId: String!, $contractAddress: String!) {
    projects_metadata(
      where: {
        project_id: { _eq: $projectId }
        contract_address: { _eq: $contractAddress }
      }
    ) {
      project_id
      name
      contract {
        address
      }
      script
      script_type_and_version
      external_asset_dependency_count
    }
  }
`);

export const getGeneratorUrl = ({
  contractAddress,
  tokenId,
}: {
  contractAddress: string;
  tokenId: string;
}): string => {
  return `https://generator.artblocks.io/${contractAddress}/${tokenId}?source=tokenRenderer`;
};

const getRequestIdFromWebm = (webmKey: string) => {
  return webmKey.replace(/^webm\//, "").replace(/\.webm$/, "");
};

const checkAndProcessFile = async (
  bucketName: string,
  objectKey: string,
  retries = 10,
  settings: RenderSettings,
  setError: Dispatch<SetStateAction<Error | null>>,
  setMessage: Dispatch<SetStateAction<string | null>>,
  setIsLoading: Dispatch<SetStateAction<boolean | null>>
) => {
  const requestId = getRequestIdFromWebm(objectKey);
  const fileName = `${requestId}.${settings.fileType}`;
  const url = `https://${bucketName}.s3.amazonaws.com/${fileName}`;

  try {
    const response = await fetch(url);

    if (!response.ok) {
      if (response.status === 404) {
        setMessage(
          `Token was rendered but your download is still loading. Please wait...`
        );

        if (retries || (settings.duration && settings.duration > 20)) {
          // Retry after 2.5 seconds. For shorter videos, retry 10x max. For 20s+ long videos, continue polling every 2.5 seconds
          setTimeout(
            () =>
              checkAndProcessFile(
                bucketName,
                objectKey,
                (retries -= 1),
                settings,
                setError,
                setMessage,
                setIsLoading
              ),
            2500
          );
        }
      } else {
        setError(new Error(`Failed to fetch ${url}: ${response.statusText}`));
      }
      return;
    } else {
      setError(null);
      setIsLoading(false);
      setMessage(null);
      downloadMediaFile(url, fileName);
      return;
    }
  } catch (err) {
    console.error("Error accessing S3:", err);
    throw err;
  }
};

export const convertToVideoFile = async (
  encodedVideoData: string,
  renderSettings: RenderSettings,
  setError: Dispatch<SetStateAction<Error | null>>,
  setMessage: Dispatch<SetStateAction<string | null>>,
  setIsLoading: Dispatch<SetStateAction<boolean | null>>
): Promise<{ response: string | null; error: string | null }> => {
  const signedURLRes = await fetch(
    `${process.env.NEXT_PUBLIC_GATEWAY_ENDPOINT}?height=${renderSettings.height}&width=${renderSettings.width}&fps=${renderSettings.fps}&filetype=${renderSettings.fileType}`,
    {
      method: "GET",
    }
  );
  const signedResponseData = await signedURLRes.json();
  const headers = {
    "Content-Type": "video/webm",
  };

  const response = await fetch(`${signedResponseData.url}`, {
    method: "PUT",
    headers,
    body: encodedVideoData,
  });
  if (!response.ok) {
    return { response: null, error: `HTTP error: ${response?.body}` };
  }

  if (renderSettings.fileType === "mp4" || renderSettings.fileType === "gif") {
    await checkAndProcessFile(
      process.env.NEXT_PUBLIC_CONVERTED_ASSETS_BUCKET || "",
      signedResponseData.key,
      5,
      renderSettings,
      setError,
      setMessage,
      setIsLoading
    );
  }

  if (response.ok) {
    return { response: "200", error: null };
  } else {
    return {
      response: null,
      error: `ERROR: Failed to invoke Lambda function: ${response}`,
    };
  }
};

export const downloadMediaFile = async (
  url: string,
  filename: string
): Promise<void> => {
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error("Error downloading the file:", error);
  }
};

export const downloadDataURL = async (
  dataURL: string,
  filename: string,
  imageType: string,
  height: string,
  width: string
): Promise<void> => {
  if (imageType === "svg") {
    dataURL = await convertSVGToPNG(dataURL, height, width);
  }
  const a = document.createElement("a");
  a.href = dataURL;
  a.download = `${filename}.png`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const isFileTypeValid = (fileType: string): boolean => {
  return ["png", "mp4", "gif"].includes(fileType);
};

function convertSVGToPNG(
  svgData: string,
  height: string,
  width: string
): Promise<string> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      reject(new Error("Failed to get canvas context"));
      return;
    }

    canvas.width = parseInt(width, 10);
    canvas.height = parseInt(height, 10);
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Use canvg to render the SVG onto the canvas
    const v = Canvg.fromString(ctx, svgData);
    v.start();
    const dataURL = canvas.toDataURL("image/png");

    resolve(dataURL);
  });
}

const getProjectIdFromTokenId = (tokenId: string): string => {
  return `${Math.floor(parseInt(tokenId) / 1000000)}`;
};

export const getProjectDetails = async (
  tokenId: string,
  contractAddress: string
): Promise<any> => {
  const projectId = getProjectIdFromTokenId(tokenId);

  const graphqlClient = getGraphQlClient();
  const variables = {
    projectId: projectId,
    contractAddress: contractAddress,
  };

  try {
    const response: any = await graphqlClient.request(
      GQL_PROJECT_QUERY,
      variables
    );

    if (response?.error?.graphQLErrors?.length) {
      console.error(response?.error?.graphQLErrors);
      return new Error(
        "Error querying project data: ",
        response?.error?.graphQLErrors
      );
    }

    return response.projects_metadata[0];
  } catch (err) {
    console.error("Error querying project data: ", err);
    return err;
  }
};
